import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type {
  AgentSession,
  AvailableAgent,
} from "@/services/types/agent-gateway";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CircleAlert,
  Clock3,
  Layers3,
  Loader2,
  Play,
  Plus,
  Search,
} from "lucide-react";
import { type Ref, useMemo, useState } from "react";
import {
  NewAgentSessionDialog,
  type NewAgentSessionInput,
} from "./NewAgentSessionDialog";
import {
  type SessionTimeFilter,
  filterAgentSessions,
  formatSessionDate,
  formatSessionDateTime,
  formatSessionDuration,
  getSessionMetrics,
  getSessionStatusOptions,
  getSessionStatusPresentation,
} from "./session-list-utils";

interface AgentSessionsDashboardProps {
  agents: AvailableAgent[];
  selectedAgent: AvailableAgent;
  sessions: AgentSession[];
  headingRef?: Ref<HTMLHeadingElement>;
  isCreating?: boolean;
  createError?: string;
  onAgentChange: (agentKey: string) => void;
  onCreateSession: (input: NewAgentSessionInput) => void;
  onResetCreateError: () => void;
}

interface CreateSessionButtonProps {
  agent: AvailableAgent;
  isCreating: boolean;
  className?: string;
  onCreate: () => void;
}

function CreateSessionButton({
  agent,
  isCreating,
  className,
  onCreate,
}: CreateSessionButtonProps) {
  const canCreate = agent.capabilities.create_sessions;

  return (
    <Button
      className={cn(
        "h-auto min-h-10 whitespace-normal py-2 text-center",
        className,
      )}
      disabled={!canCreate || isCreating}
      onClick={onCreate}
      size="lg"
      type="button"
    >
      {isCreating ? (
        <Loader2 aria-hidden="true" className="animate-spin" />
      ) : (
        <Plus aria-hidden="true" />
      )}
      {isCreating ? "Starting session…" : `New ${agent.display_name} Session`}
    </Button>
  );
}

function SessionStatusBadge({ status }: { status: string }) {
  const presentation = getSessionStatusPresentation(status);
  const styles = {
    completed: {
      badge: "border-green-200 bg-green-50 text-green-700",
      dot: "bg-green-600",
    },
    failed: {
      badge: "border-red-200 bg-red-50 text-red-700",
      dot: "bg-red-600",
    },
    in_progress: {
      badge: "border-blue-200 bg-blue-50 text-blue-700",
      dot: "bg-blue-600",
    },
    other: {
      badge: "border-slate-200 bg-slate-50 text-slate-700",
      dot: "bg-slate-500",
    },
  }[presentation.category];

  return (
    <Badge
      className={`shrink-0 gap-2 rounded-full ${styles.badge}`}
      variant="outline"
    >
      <span
        aria-hidden="true"
        className={`size-1.5 rounded-full ${styles.dot}`}
      />
      {presentation.label}
    </Badge>
  );
}

function SessionMetricCard({
  icon: Icon,
  label,
  value,
  description,
}: {
  icon: typeof Layers3;
  label: string;
  value: number;
  description: string;
}) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs md:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-slate-500 text-sm">{label}</p>
          <p className="mt-2 font-semibold text-2xl text-slate-900">{value}</p>
        </div>
        <span className="flex size-9 items-center justify-center rounded-full bg-brand-lightGreen text-brand-darkGreen">
          <Icon aria-hidden="true" className="size-4" />
        </span>
      </div>
      <p className="mt-2 text-slate-400 text-xs">{description}</p>
    </article>
  );
}

function SessionsTable({
  agentKey,
  sessions,
}: {
  agentKey: string;
  sessions: AgentSession[];
}) {
  return (
    <div
      className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white lg:block"
      data-testid="desktop-session-table"
    >
      <Table aria-label="Agent sessions">
        <TableHeader className="bg-slate-50/80">
          <TableRow className="hover:bg-slate-50/80">
            <TableHead>Session</TableHead>
            <TableHead>Session ID</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Started</TableHead>
            <TableHead>
              <span className="sr-only">Open session</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session) => (
            <TableRow key={session.id}>
              <TableCell>
                <Link
                  className="block font-medium text-slate-900 [overflow-wrap:anywhere] hover:text-brand-darkGreen hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  params={{ agentKey, sessionId: session.id }}
                  to="/sales-agent/$agentKey/$sessionId"
                >
                  {session.title || "Untitled session"}
                </Link>
                <p className="mt-1 text-slate-500 text-xs [overflow-wrap:anywhere]">
                  by {session.created_by.name ?? "Unknown user"}
                </p>
              </TableCell>
              <TableCell className="max-w-48 break-all font-mono text-slate-500 text-xs">
                {session.id}
              </TableCell>
              <TableCell>{formatSessionDuration(session)}</TableCell>
              <TableCell>
                <SessionStatusBadge status={session.status} />
              </TableCell>
              <TableCell>{formatSessionDateTime(session.created_at)}</TableCell>
              <TableCell className="text-right">
                <Button asChild size="icon" variant="ghost">
                  <Link
                    aria-label={`Open ${session.title || "untitled session"}`}
                    params={{ agentKey, sessionId: session.id }}
                    to="/sales-agent/$agentKey/$sessionId"
                  >
                    <ArrowRight aria-hidden="true" />
                  </Link>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SessionCards({
  agentKey,
  sessions,
}: {
  agentKey: string;
  sessions: AgentSession[];
}) {
  return (
    <div className="grid gap-3 lg:hidden" data-testid="mobile-session-cards">
      {sessions.map((session) => (
        <Link
          aria-label={`Open ${session.title || "untitled session"}, status ${getSessionStatusPresentation(session.status).label}`}
          className="block min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-xs transition-colors hover:border-brand-darkGreen/40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          key={session.id}
          params={{ agentKey, sessionId: session.id }}
          to="/sales-agent/$agentKey/$sessionId"
        >
          <article>
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-slate-900 [overflow-wrap:anywhere]">
                  {session.title || "Untitled session"}
                </h3>
                <p className="mt-1 text-slate-500 text-xs [overflow-wrap:anywhere]">
                  by {session.created_by.name ?? "Unknown user"} ·{" "}
                  {formatSessionDate(session.created_at)}
                </p>
              </div>
              <SessionStatusBadge status={session.status} />
            </div>

            <dl className="mt-4 grid min-w-0 grid-cols-2 gap-3 border-slate-100 border-t pt-3 text-xs">
              <div className="min-w-0">
                <dt className="text-slate-400">Session ID</dt>
                <dd className="mt-1 break-all font-mono text-slate-700">
                  {session.id}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">Duration</dt>
                <dd className="mt-1 text-slate-700">
                  {formatSessionDuration(session)}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-slate-400">Started</dt>
                <dd className="mt-1 text-slate-700">
                  {formatSessionDateTime(session.created_at)}
                </dd>
              </div>
            </dl>
          </article>
        </Link>
      ))}
    </div>
  );
}

export function AgentSessionsDashboard({
  agents,
  selectedAgent,
  sessions,
  headingRef,
  isCreating = false,
  createError,
  onAgentChange,
  onCreateSession,
  onResetCreateError,
}: AgentSessionsDashboardProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [time, setTime] = useState<SessionTimeFilter>("all");
  const metrics = useMemo(() => getSessionMetrics(sessions), [sessions]);
  const statusOptions = useMemo(
    () => getSessionStatusOptions(sessions),
    [sessions],
  );
  const filteredSessions = useMemo(
    () => filterAgentSessions(sessions, { query, status, time }),
    [query, sessions, status, time],
  );
  const clearFilters = () => {
    setQuery("");
    setStatus("all");
    setTime("all");
  };
  const openCreateDialog = () => {
    onResetCreateError();
    setCreateDialogOpen(true);
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto overflow-x-hidden bg-slate-50/40 p-4 pb-[calc(7rem+env(safe-area-inset-bottom))] md:p-8 md:pb-24 lg:pb-8">
      <header className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1
            className="rounded-sm font-semibold text-2xl text-slate-900 outline-none [overflow-wrap:anywhere] focus-visible:ring-[3px] focus-visible:ring-ring/50"
            ref={headingRef}
            tabIndex={-1}
          >
            AI Agents
          </h1>
          <p className="mt-1 text-slate-500 text-sm md:text-base">
            Run and resume supplier-owned sessions with your available agents.
          </p>
        </div>
        <CreateSessionButton
          agent={selectedAgent}
          className="hidden lg:inline-flex"
          isCreating={isCreating}
          onCreate={openCreateDialog}
        />
      </header>

      {!selectedAgent.capabilities.create_sessions && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">
          New session creation is currently unavailable for{" "}
          {selectedAgent.display_name}. Existing sessions remain accessible.
        </div>
      )}

      <section
        aria-label="Session summary"
        className="grid grid-cols-2 gap-3 lg:grid-cols-3"
      >
        <SessionMetricCard
          description="Available history"
          icon={Layers3}
          label="Total sessions"
          value={metrics.total}
        />
        <SessionMetricCard
          description="Running now"
          icon={Play}
          label="In progress"
          value={metrics.inProgress}
        />
        <SessionMetricCard
          description="All time"
          icon={CircleAlert}
          label="Failed"
          value={metrics.failed}
        />
      </section>

      <section aria-labelledby="sessions-heading" className="space-y-4">
        <div className="border-slate-200 border-b">
          <h2
            className="inline-block border-brand-darkGreen border-b-2 px-1 pb-3 font-medium text-slate-900"
            id="sessions-heading"
          >
            All sessions
          </h2>
        </div>

        <fieldset className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center">
          <legend className="sr-only">Session filters</legend>
          <div className="relative flex-1">
            <Search
              aria-hidden="true"
              className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400"
            />
            <Input
              aria-label="Search sessions"
              className="h-10 bg-white pl-9"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by session, ID, creator, or date"
              type="search"
              value={query}
            />
          </div>
          <div
            className={cn(
              "grid grid-cols-2 gap-3 lg:flex",
              agents.length > 1 && "sm:grid-cols-3",
            )}
          >
            {agents.length > 1 && (
              <>
                <label className="sr-only" htmlFor="agent-selector">
                  Agent
                </label>
                <select
                  className="col-span-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:col-span-1"
                  id="agent-selector"
                  onChange={(event) => onAgentChange(event.target.value)}
                  value={selectedAgent.key}
                >
                  {agents.map((agent) => (
                    <option key={agent.key} value={agent.key}>
                      {agent.display_name}
                    </option>
                  ))}
                </select>
              </>
            )}
            <label className="sr-only" htmlFor="session-status-filter">
              Filter by status
            </label>
            <select
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              id="session-status-filter"
              onChange={(event) => setStatus(event.target.value)}
              value={status}
            >
              <option value="all">All statuses</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor="session-time-filter">
              Filter by time
            </label>
            <select
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              id="session-time-filter"
              onChange={(event) =>
                setTime(event.target.value as SessionTimeFilter)
              }
              value={time}
            >
              <option value="all">All time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          </div>
        </fieldset>

        <p aria-live="polite" className="text-slate-500 text-sm">
          Showing {filteredSessions.length} of {sessions.length} sessions
        </p>

        {sessions.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-slate-200 border-dashed bg-white px-6 py-14 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-brand-lightGreen text-brand-darkGreen">
              <Clock3 aria-hidden="true" className="size-5" />
            </span>
            <h3 className="mt-4 font-semibold text-lg text-slate-900">
              No sessions yet
            </h3>
            <p className="mt-1 max-w-md text-slate-500 text-sm">
              Start a {selectedAgent.display_name} session to begin working with
              your supplier’s assigned agent.
            </p>
            <CreateSessionButton
              agent={selectedAgent}
              className="mt-5"
              isCreating={isCreating}
              onCreate={openCreateDialog}
            />
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-slate-200 border-dashed bg-white px-6 py-12 text-center">
            <Search aria-hidden="true" className="size-6 text-slate-400" />
            <h3 className="mt-3 font-semibold text-slate-900">
              No matching sessions
            </h3>
            <p className="mt-1 text-slate-500 text-sm">
              Try a different search, status, or time range.
            </p>
            <Button
              className="mt-4"
              onClick={clearFilters}
              type="button"
              variant="outline"
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <>
            <SessionsTable
              agentKey={selectedAgent.key}
              sessions={filteredSessions}
            />
            <SessionCards
              agentKey={selectedAgent.key}
              sessions={filteredSessions}
            />
          </>
        )}
      </section>

      <div className="fixed inset-x-0 bottom-0 z-30 border-slate-200 border-t bg-white/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur lg:hidden">
        <CreateSessionButton
          agent={selectedAgent}
          className="w-full"
          isCreating={isCreating}
          onCreate={openCreateDialog}
        />
      </div>

      <NewAgentSessionDialog
        agents={agents.filter(
          (agent) =>
            agent.status === "available" && agent.capabilities.create_sessions,
        )}
        createError={createError}
        defaultAgentKey={selectedAgent.key}
        isCreating={isCreating}
        onOpenChange={setCreateDialogOpen}
        onStartSession={onCreateSession}
        open={createDialogOpen}
      />
    </div>
  );
}

export function AgentSessionsDashboardSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Loading agent sessions"
      className="flex flex-1 flex-col gap-6 overflow-hidden bg-slate-50/40 p-4 md:p-8"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-36" />
          <Skeleton className="h-5 w-72 max-w-full" />
        </div>
        <Skeleton className="hidden h-10 w-64 lg:block" />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {[0, 1, 2].map((item) => (
          <Skeleton className="h-32 rounded-xl" key={item} />
        ))}
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="grid gap-3 lg:grid-cols-[1fr_12rem_12rem]">
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
      </div>
      <Skeleton className="h-72 rounded-xl" />
    </div>
  );
}
